import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Alert,
  Box,
  Tooltip,
} from '@mui/material';
import { clickPerformanceApi } from '../../services/api';
import { getComparator } from '../../utils/tableSort';

const fmtCount = (v) => (v ?? 0).toLocaleString();
const fmtPct = (v) => v != null ? `${(v * 100).toFixed(2)}%` : '—';
const fmtHours = (h) => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h.toFixed(1)}h`;
};

// cohort_conversion_rate: partner's network-wide rate (same window, all partners combined).
// Sent table  → partner's recv rate across the whole network (how well they convert anyone's clicks).
// Received table → partner's send rate across the whole network (how well the clicks they send convert).
// Hidden when partner has < 5 network clicks in that role.
const COHORT_TOOLTIP_SENT = "This partner's conversion rate across the whole network as a receiver — how well they convert clicks from any sender. Benchmark for your pair's conv%.";
const COHORT_TOOLTIP_RECV = "This partner's conversion rate across the whole network as a sender — how well the clicks they send convert at any retailer. Benchmark for your pair's conv%.";

function makeCols(cohortTooltip) {
  return [
    { id: 'partner_name',          label: 'Partner',   align: 'left' },
    { id: 'clicks',                label: 'Clicks',    align: 'right', format: fmtCount },
    { id: 'orders',                label: 'Orders',    align: 'right', format: fmtCount },
    { id: 'conversion_rate',       label: 'Conv%',     align: 'right', format: fmtPct },
    { id: 'cohort_conversion_rate',label: 'Net conv%', align: 'right', format: fmtPct, tooltip: cohortTooltip },
    { id: 'avg_conversion_hours',  label: 'Avg t',     align: 'right', format: fmtHours },
  ];
}

function PartnerTable({ rows, title, cohortTooltip }) {
  const [orderBy, setOrderBy] = useState('clicks');
  const [order, setOrder] = useState('desc');

  const cols = makeCols(cohortTooltip);
  const sorted = [...(rows || [])].sort(getComparator(order, orderBy));

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'desc' ? 'asc' : 'desc');
    setOrderBy(col);
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No data for this period.</Typography>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', px: '6px' }, '& td:first-of-type, & th:first-of-type': { pl: 0 } }}>
            <TableHead>
              <TableRow>
                {cols.map((col) => (
                  <TableCell key={col.id} align={col.align}>
                    {col.tooltip ? (
                      <Tooltip title={col.tooltip} arrow placement="top">
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : 'desc'}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </Tooltip>
                    ) : (
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : 'desc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((row) => (
                <TableRow key={row.partner_id} hover>
                  {cols.map((col) => (
                    <TableCell key={col.id} align={col.align}>
                      {col.format ? col.format(row[col.id]) : row[col.id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

const RetailerPartnerDrilldown = ({ open, onClose, retailerId, retailerName, fromDate, toDate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !retailerId) return;
    setLoading(true);
    setError(null);
    clickPerformanceApi
      .getRetailerPartners(retailerId, { from_date: fromDate, to_date: toDate })
      .then((res) => {
        if (res.success) setData(res.data);
        else setError(res.error || 'Failed to load partner data');
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, retailerId, fromDate, toDate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Partner breakdown — {retailerName || retailerId}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {data && !loading && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <PartnerTable rows={data.received} title="Received clicks from" cohortTooltip={COHORT_TOOLTIP_RECV} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PartnerTable rows={data.sent} title="Sent clicks to" cohortTooltip={COHORT_TOOLTIP_SENT} />
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RetailerPartnerDrilldown;
